import React, { useEffect, useRef, useState } from "react";
import {
  PrimaryGrid,
  GridColumn,
  PrimaryButton,
  dialogStore,
  EnumSingleSelectFieldV2,
  AppToasterV2,
  LoadingV2,
  GridFieldColumn,
} from "@ucl/library";
import type {
  BaseGridModel,
  EnumSingleSelectFieldV2Props,
  EnumSingleSelectFieldValue,
  PrimaryGridModule,
} from "@ucl/library";
import variables from "../config/variables";
import { useAuthHeadersRequired } from "../lib/auth/authUtils";
import "./BankerApplicationsPage.scss";
import { useNavigate } from "react-router-dom";
import { NewApplicationForm } from "@/components/NewApplication/NewApplicationForm";
import { apiClient } from "@/api/apiClient";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import { createBoltTaskApiClient } from "@bolt/task/src/lib/apiClients/boltTaskApiClient";
import { EntityTypes } from "@/types/EntityTypeMapping";
import { AppUser } from "@/contexts/UserContext/types";
import { RemainingFields } from "@/components/RemainingFields/RemainingFields";
import { useBoltApiClient } from "@bolt/core";
import moment from "moment";
import { FinancialApplicationSection } from "@/types/enums";
import { BankerValidationErrorsBadge } from "@/components/BankerApplicationDetails/BankerValidationErrorsBadge";
import { ValidationErrorDisplayMode } from "@/components/ValidationErrorsCounts/ValidationErrorsCounts";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
// import { useBankerFinancialApplication } from "@/hooks/useBankerFinancialApplication";

interface FinancialApplicationModel {
  id: string;
  primaryCustomer: string;
  requiredFieldCount: number;
  requiredFieldRemainingCount: number;
  remainingFieldsFormatted: string; // e.g., "4/5"
  requiredFieldsRemaining_AsString: string; // e.g., "4/5"
  requiredFieldsRemainingRatio: number;
  outstandingTasksRatio: number;
  outstandingTasksCount: number;
  totalTasksCount: number;
  outstandingTasks_AsString: string; // e.g., "4/5"
  homeAddressCity: string;
  homeAddressState: string;
  accountTypes_AsString: string;
  productTypes_AsString: string;
  companyAssigneeFullName: string;
  createdAt?: string;
  updatedAt?: string;
}

export const BankerApplicationsPage: React.FC = () => {
  const gridKey = "financial-applications-grid";
  const getAuthRequestHeadersRequired = useAuthHeadersRequired();
  const navigate = useNavigate();
  const gridRef = useRef<PrimaryGridModule>(null);
  const boltApiClient = useBoltApiClient();
  const boltTaskApiClient = createBoltTaskApiClient(boltApiClient);
  const financialApplicationApiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);
  // const { createSeedFinancialApplication } = useBankerFinancialApplication();
  const [columnsConfiguration, setColumnsConfiguration] = useState<GridColumn[]>();

  useEffect(() => {
    financialApplicationApiClient.getCompanyAssignees().then((response) => {
      setColumnsConfiguration(getColumnsConfiguration(response));
    });
  }, []);

  // Basic columns configuration - using type assertion for now
  const getColumnsConfiguration = (companyAssignees: AppUser[]): GridColumn[] => {
    return [
      new GridColumn({
        field: "id",
        headerText: "ID",
        type: "string",
        visible: false,
      }),
      new GridColumn({
        field: "primaryCustomer",
        headerText: "Primary Customer",
        type: "string",
      }),
      new GridColumn({
        field: "accountTypes_AsString",
        headerText: "Account Type",
        type: "string",
        maxWidth: 300,
        template: (data: FinancialApplicationModel) => {
          return data.accountTypes_AsString;
        },
        // template: (data: FinancialApplicationModel) => {
        //   return (
        //     data.accountTypes_AsString
        //       ?.split(",")
        //       .map((account: string) => <div key={account}>{account}</div>) || []
        //   );
        // },
      }),
      new GridColumn({
        field: "productTypes_AsString",
        headerText: "Product",
        type: "string",
        maxWidth: 300,
        template: (data: FinancialApplicationModel) => {
          return data.productTypes_AsString;
        },
        // template: (data: FinancialApplicationModel) => {
        //   return (
        //     data.productTypes_AsString
        //       ?.split(",")
        //       .map((product: string) => <div key={product}>{product}</div>) || []
        //   );
        // },
      }),
      new GridColumn({
        field: "requiredFieldsRemainingRatio",
        headerText: "Remaining Fields",
        type: "string",
        template: (data: FinancialApplicationModel) => {
          return (
            <BankerValidationErrorsBadge
              financialApplicationId={data.id}
              section={FinancialApplicationSection.AllSections}
              renderZeroErrors={true}
              showNavigateToField={false}
              showSectionTitle={true}
              popoverClassName="banker-applications-page__popover"
              displayMode={ValidationErrorDisplayMode.RemainingFromTotal}
            />
          );
        },
        textAlign: "Center",
      }),
      new GridColumn({
        field: "outstandingTasksRatio",
        headerText: "Remaining Tasks",
        type: "string",
        template: (data: FinancialApplicationModel) => {
          return (
            <RemainingFields
              remainingCount={data.outstandingTasksCount}
              totalCount={data.totalTasksCount}
              showFieldDetails={true}
              remainingFieldsPromiseFactory={getRemainingTasks(data.id)}
              popoverLabel="Remaining Tasks"
            />
          );
        },
        textAlign: "Center",
      }),
      new GridColumn({
        field: "homeAddressCity",
        headerText: "City",
        type: "string",
      }),
      new GridColumn({
        field: "homeAddressState",
        headerText: "State",
        type: "string",
      }),
      new GridFieldColumn<FinancialApplicationModel, EnumSingleSelectFieldV2Props>({
        field: "companyAssigneeFullName",
        headerText: "Company Assignee",
        type: "string",
        fieldId: "companyAssigneeId",
        fieldComponent: EnumSingleSelectFieldV2,
        fieldProps: {
          type: "EnumSingleSelect",
          showClearButton: false,
          optionValues: companyAssignees.map((assignee) => ({
            value: assignee.id,
            label: assignee.displayName,
          })),
        },
        fieldOnValueChange: (
          data: FinancialApplicationModel,
          newValue: EnumSingleSelectFieldValue
        ) => {
          handleCompanyAssigneeChange(data.id, newValue as string);
        },
      }),
      new GridColumn({
        field: "createdAt",
        headerText: "Created At",
        type: "datetime",
        format: { format: "MM/dd/yyyy hh:mm:ss a", type: "datetime" },
      }),
      new GridColumn({
        field: "updatedAt",
        headerText: "Updated At",
        type: "datetime",
        format: { format: "MM/dd/yyyy hh:mm:ss a", type: "datetime" },
      }),
    ];
  };

  const getRemainingTasks = (financialApplicationId: string) => {
    return async () => {
      const response = await boltTaskApiClient.getTasks(
        EntityTypes.FinancialApplication.EntityType,
        financialApplicationId,
        true
      );
      return response.map(
        (task) => `${task.taskTemplateName} (Due: ${moment(task.dueDate).format("M/D/YYYY")})`
      );
    };
  };

  const handleRecordClick = (args: { rowData?: { id?: string } }) => {
    const id = args?.rowData?.id;
    if (id) {
      navigate(`/banker/applications/${id}`);
    }
  };

  const handleCompanyAssigneeChange = (applicationId: string, companyAssigneeId: string) => {
    financialApplicationApiClient
      .updateFinancialApplicationCompanyAssignee(applicationId, companyAssigneeId)
      .then(() => {
        AppToasterV2.showSuccessAppToaster("Company assignee updated");
      })
      .catch((error: any) => {
        AppToasterV2.showFailedActionToaster("Error updating company assignee");
        console.error(error);
      });
  };

  const handleNewApplicationClick = () => {
    const dialogId = "new-application-dialog";

    dialogStore.openDialog({
      id: dialogId,
      header: "Create a New Application",
      allowDragging: false,
      width: "600px",
      height: "500px",
      content: () => (
        <NewApplicationForm
          isOpen={true}
          onClose={() => {
            dialogStore.closeDialog(dialogId);
          }}
          onSuccess={(applicationId) => {
            dialogStore.closeDialog(dialogId);
            // Refresh the grid to show the new application
            gridRef.current?.getGridModule()?.getGridRef()?.refresh();
            navigate(`/banker/applications/${applicationId}`);
          }}
        />
      ),
      close: () => {
        dialogStore.closeDialog(dialogId);
      },
    });
  };

  // const handleCreateSeedApplicationClick = async () => {
  //   try {
  //     const application = await createSeedFinancialApplication();
  //     if (application) {
  //       AppToasterV2.showSuccessAppToaster("Seed application created successfully");
  //       // Refresh the grid to show the new application
  //       gridRef.current?.getGridModule()?.getGridRef()?.refresh();
  //       // Navigate to the new application
  //       navigate(`/banker/applications/${application.id}`);
  //     }
  //   } catch (error) {
  //     console.error("Failed to create seed application:", error);
  //     AppToasterV2.showFailedActionToaster("Failed to create seed application");
  //   }
  // };

  // Configuration for PrimaryGrid views
  const viewsSettings = {
    gridTitle: "Dashboard",
    showGridViewPicker: true,
    shouldCreateAutoView: true,
    defaultGridViewTitle: "Default View",
    pathToUserViewApi: "/bolt/userview",
  };

  // Grid configuration for PrimaryGrid
  const gridConfiguration: Readonly<BaseGridModel<FinancialApplicationModel>> = {
    // showGridFilters: true,
    // allowSearch: true,
    onRecordClick: handleRecordClick,
    enableColumnVirtualization: false,
  };

  if (!columnsConfiguration) {
    return <LoadingV2 loadingType="spinner" />;
  }

  return (
    <div className="banker-page">
      {/* <div className="banker-page__header">
        <h1 className="banker-page__title">Financial Applications</h1>
        <p className="banker-page__subtitle">Manage and review customer financial applications</p>
      </div> */}

      <div className="banker-page__content">
        <div className="banker-page__content-header">
          {/* <PrimaryButton text="Create Seed Application" onClick={handleCreateSeedApplicationClick} /> */}
          <PrimaryButton
            text="Create New Application"
            onClick={handleNewApplicationClick}
            iconProps={{
              icon: faPlus,
            }}
            iconPosition="left"
          />
        </div>
        <PrimaryGrid<FinancialApplicationModel, object>
          ref={gridRef}
          gridKey={gridKey}
          apiClient={apiClient}
          columnsConfiguration={columnsConfiguration}
          getAuthRequestHeader={getAuthRequestHeadersRequired}
          odataUrl={`${variables.apiBaseUrl}/odata/financialapplicationsodata`}
          viewsSettings={viewsSettings}
          gridConfiguration={gridConfiguration}
          showGridFilters={true}
          allowSearch={true}
          metricsSettings={{
            url: `${variables.apiBaseUrl}/api/financial-application`,
            columns: 2,
            languageMapping: {
              financialApplicationCount: "Count of Applications",
              totalInitialDeposits: "Total of Initial Deposit Amounts",
            },
          }}
          gridHeightSettings={{
            autoHeightOffset: 700,
          }}
          showInCardContainer={true}
        />
      </div>
    </div>
  );
};

export default BankerApplicationsPage;
